import api, { publicApi } from './api';

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
  );

const productService = {
  /**
   * Obtener todos los productos (admin)
   */
  async getAll(params = {}) {
    try {
      const response = await api.get('/products', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener productos'
      };
    }
  },

  /**
   * Obtener productos para catálogo público (sin autenticación)
   */
  async getCatalogPublic(params = {}) {
    try {
      // Intenta primero con el endpoint de catálogo público
      const response = await publicApi.get('/products/catalog', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      // Si falla, intenta con el endpoint regular sin límite
      try {
        const response = await publicApi.get('/products', { params: { ...params, limit: 1000 } });
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination
        };
      } catch (fallbackError) {
        return {
          success: false,
          message: fallbackError.response?.data?.message || 'Error al obtener catálogo'
        };
      }
    }
  },

  /**
   * Obtener productos para catálogo (público)
   */
  async getCatalog(params = {}) {
    try {
      const response = await api.get('/products/catalog', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener catálogo'
      };
    }
  },

  /**
   * Obtener producto por ID
   */
  async getById(id) {
    try {
      const response = await api.get(`/products/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener producto'
      };
    }
  },

  /**
   * Crear producto
   */
  async create(productData) {
    try {
      const response = await api.post('/products', productData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear producto'
      };
    }
  },

  /**
   * Actualizar producto
   */
  async update(id, productData) {
    try {
      const response = await api.put(`/products/${id}`, productData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar producto'
      };
    }
  },

  /**
   * Actualizar stock
   */
  async updateStock(id, stock, operation = 'set') {
    try {
      const response = await api.patch(`/products/${id}/stock`, {
        stock,
        operation
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar stock'
      };
    }
  },

  /**
   * Eliminar producto
   */
  async delete(id) {
    try {
      const response = await api.delete(`/products/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar producto'
      };
    }
  },

  async exportFile() {
    try {
      const response = await api.get('/products/export/file', { responseType: 'blob' });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: 'Error al exportar productos' };
    }
  },

  async importFile(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/products/import/file', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      return { success: true, data: response.data.data, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al importar productos' };
    }
  },

  async bulkDelete(ids) {
    try {
      const response = await api.post('/products/bulk-delete', { ids });
      return { success: true, data: response.data.data, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al eliminar productos' };
    }
  },

  async bulkUpdateVisibility(ids, showInCatalog) {
    try {
      const response = await api.patch('/products/bulk-visibility', { ids, showInCatalog });
      return { success: true, data: response.data.data, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al actualizar la visibilidad' };
    }
  },

  /**
   * Verificar si un código está disponible
   */
  async checkCode(code, currentProductId = null) {
    try {
      const params = { code };
      if (currentProductId) {
        params.excludeId = currentProductId;
      }
      const response = await api.get('/products/check-code', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al verificar código'
      };
    }
  },

  /**
   * Obtener productos eliminados
   */
  async getDeleted(params = {}) {
    try {
      const response = await api.get('/products/deleted/list', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener productos eliminados'
      };
    }
  },

  /**
   * Restaurar producto eliminado
   */
  async restore(id, newCode = null) {
    try {
      const body = newCode ? { newCode } : {};
      const response = await api.post(`/products/${id}/restore`, body);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        info: response.data.info
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al restaurar producto',
        error: error.response?.data?.error,
        data: error.response?.data?.data
      };
    }
  },

  /**
   * Obtener categorías disponibles
   */
  async getCategories() {
    try {
      const response = await publicApi.get('/categories');
      return {
        success: true,
        data: sortByName(response.data.data)
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener categorías'
      };
    }
  },

  /**
   * Obtener marcas disponibles
   */
  async getBrands() {
    try {
      const response = await publicApi.get('/brands');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener marcas'
      };
    }
  }
};

export default productService;


